export type ImpressionVerdict = 'yes' | 'maybe' | 'no';

export type ImpressionTag =
  | 'atmosphere'
  | 'service'
  | 'food'
  | 'drinks'
  | 'music'
  | 'crowd'
  | 'price'
  | 'location'
  | 'cleanliness'
  | 'wifi'
  | 'parking'
  | 'accessibility'
  | 'family_friendly'
  | 'pet_friendly'
  | 'late_night'
  | 'quiet'
  | 'lively'
  | 'romantic'
  | 'business'
  | 'outdoor_seating';

export type GuestImpression = {
  id: string;
  visit_id?: string;
  verdict: ImpressionVerdict;
  text: string | null;
  is_public: boolean;
  created_at: string | null;
  updated_at: string | null;
  tags: ImpressionTag[];
  user: {
    id: string;
    display_name: string | null;
  };
  venue: {
    id: string;
    display_name: string | null;
  };
  reply: null | {
    text: string;
    created_at: string | null;
  };
};

export type ImpressionCollectionResponse = {
  data: GuestImpression[];
  links?: {
    first?: string | null;
    last?: string | null;
    prev?: string | null;
    next?: string | null;
  };
  meta?: {
    current_page?: number;
    from?: number | null;
    path?: string;
    per_page?: number;
    to?: number | null;
  };
};

export type ImpressionPayload = {
  verdict: ImpressionVerdict;
  text: string | null;
  tags: ImpressionTag[];
  is_public: boolean;
};

type ErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
  };
};

type CsrfResponse = {
  csrf_token: string;
};

export type ImpressionFocusResolvedDetail = {
  venueId: string;
  impressionId: string;
  found: boolean;
};

export const IMPRESSION_FOCUS_RESOLVED_EVENT = 'hopper:impression-focus-resolved';

export class ImpressionApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly fields: Record<string, string[]>;

  constructor(status: number, payload: ErrorPayload) {
    super(payload.error?.message ?? `Impressions API failed: ${status}`);
    this.name = 'ImpressionApiError';
    this.status = status;
    this.code = payload.error?.code ?? null;
    this.fields = payload.error?.fields ?? {};
  }
}

export const IMPRESSION_TAGS: ImpressionTag[] = [
  'atmosphere',
  'service',
  'food',
  'drinks',
  'music',
  'crowd',
  'price',
  'location',
  'cleanliness',
  'wifi',
  'parking',
  'accessibility',
  'family_friendly',
  'pet_friendly',
  'late_night',
  'quiet',
  'lively',
  'romantic',
  'business',
  'outdoor_seating',
];

export const IMPRESSION_TAG_LABELS: Record<ImpressionTag, string> = {
  atmosphere: 'Атмосфера',
  service: 'Сервис',
  food: 'Еда',
  drinks: 'Напитки',
  music: 'Музыка',
  crowd: 'Публика',
  price: 'Цена',
  location: 'Расположение',
  cleanliness: 'Чистота',
  wifi: 'Wi‑Fi',
  parking: 'Парковка',
  accessibility: 'Доступность',
  family_friendly: 'С детьми',
  pet_friendly: 'С питомцами',
  late_night: 'Допоздна',
  quiet: 'Тихо',
  lively: 'Оживлённо',
  romantic: 'Романтика',
  business: 'Для встреч',
  outdoor_seating: 'На улице',
};

export const IMPRESSION_VERDICT_LABELS: Record<ImpressionVerdict, string> = {
  yes: 'Да',
  maybe: 'Возможно',
  no: 'Нет',
};

let csrfToken: string | null = null;

async function ensureCsrf(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new ImpressionApiError(response.status, {});
  }

  const body = (await response.json()) as CsrfResponse;
  csrfToken = body.csrf_token;
  return csrfToken;
}

async function impressionFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
    if (init.body) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(url, {
    ...init,
    credentials: 'same-origin',
    headers,
  });

  const payload = await response.json().catch(() => ({})) as T & ErrorPayload;

  if (!response.ok) {
    throw new ImpressionApiError(response.status, payload);
  }

  return payload;
}

function focusedImpressionId(venueId: string, page: number, routeReferences: string[]): string | null {
  if (page !== 1) return null;

  const match = window.location.pathname.match(/^\/venue\/([^/]+)\/?$/);
  if (!match) return null;

  let pathVenueReference = match[1];
  try {
    pathVenueReference = decodeURIComponent(pathVenueReference);
  } catch {
    // Keep the raw route value when it is not URI-decodable.
  }

  const acceptedReferences = new Set([
    venueId,
    ...routeReferences.map((value) => value.trim()).filter(Boolean),
  ]);
  if (!acceptedReferences.has(pathVenueReference)) return null;

  const value = new URL(window.location.href).searchParams.get('impression')?.trim();
  return value || null;
}

function clearImpressionFocusParameter(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('impression')) return;
  url.searchParams.delete('impression');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function dispatchImpressionFocusResolved(detail: ImpressionFocusResolvedDetail): void {
  window.dispatchEvent(new CustomEvent<ImpressionFocusResolvedDetail>(IMPRESSION_FOCUS_RESOLVED_EVENT, { detail }));
}

function fetchVenueImpressionsPage(venueId: string, page: number): Promise<ImpressionCollectionResponse> {
  return impressionFetch<ImpressionCollectionResponse>(
    `/api/v1/venues/${encodeURIComponent(venueId)}/impressions?page=${page}`,
  );
}

async function fetchVenueImpressionsForFocus(
  venueId: string,
  impressionId: string,
): Promise<ImpressionCollectionResponse> {
  const collected: GuestImpression[] = [];
  let page = 1;
  let lastResponse: ImpressionCollectionResponse | null = null;

  try {
    for (;;) {
      const response = await fetchVenueImpressionsPage(venueId, page);
      lastResponse = response;
      collected.push(...response.data);

      if (!response.links?.next) break;
      page += 1;
    }
  } catch (error) {
    dispatchImpressionFocusResolved({ venueId, impressionId, found: false });
    clearImpressionFocusParameter();

    if (!lastResponse) throw error;

    return {
      ...lastResponse,
      data: collected,
      links: { ...lastResponse.links, next: null },
    };
  }

  const target = collected.find((item) => item.id === impressionId) ?? null;
  if (!target) {
    dispatchImpressionFocusResolved({ venueId, impressionId, found: false });
    clearImpressionFocusParameter();
    return {
      ...(lastResponse ?? { data: [] }),
      data: collected,
      links: { ...(lastResponse?.links ?? {}), next: null },
    };
  }

  const ordered = [target, ...collected.filter((item) => item.id !== impressionId)];
  dispatchImpressionFocusResolved({ venueId, impressionId, found: true });

  return {
    ...(lastResponse ?? { data: [] }),
    data: ordered,
    links: { ...(lastResponse?.links ?? {}), next: null },
  };
}

export function fetchMineImpressions(page = 1): Promise<ImpressionCollectionResponse> {
  return impressionFetch<ImpressionCollectionResponse>(`/api/v1/me/impressions?page=${page}`);
}

export function fetchVenueImpressions(
  venueId: string,
  page = 1,
  routeReferences: string[] = [],
): Promise<ImpressionCollectionResponse> {
  const impressionId = focusedImpressionId(venueId, page, routeReferences);
  return impressionId
    ? fetchVenueImpressionsForFocus(venueId, impressionId)
    : fetchVenueImpressionsPage(venueId, page);
}

export function createImpression(
  visitId: string,
  payload: ImpressionPayload,
): Promise<{ data: GuestImpression }> {
  return impressionFetch<{ data: GuestImpression }>(
    `/api/v1/visits/${encodeURIComponent(visitId)}/impression`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function updateImpression(
  impressionId: string,
  payload: ImpressionPayload,
): Promise<{ data: GuestImpression }> {
  return impressionFetch<{ data: GuestImpression }>(
    `/api/v1/impressions/${encodeURIComponent(impressionId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export function deleteImpression(impressionId: string): Promise<{ data: { deleted: boolean } }> {
  return impressionFetch<{ data: { deleted: boolean } }>(
    `/api/v1/impressions/${encodeURIComponent(impressionId)}`,
    { method: 'DELETE' },
  );
}

export function canEditImpression(createdAt: string | null): boolean {
  if (!createdAt) {
    return false;
  }

  const created = new Date(createdAt).getTime();
  return Number.isFinite(created) && Date.now() - created < 24 * 60 * 60 * 1000;
}

export function formatImpressionDate(value: string | null): string {
  if (!value) {
    return 'Дата не указана';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
